<template>
  <Modal v-if="show" :show="show" @close="show = false" class="compare-modal">
    <div class="component">
      <h5 class="component-title">Compare {{ type }}s</h5>

      <div class="compare-table">
        <div
          v-for="(header, index) in headers"
          class="compare-col header-col"
          :style="{ order: index + 1 }"
          :key="header.key"
        >
          <template v-if="index === 0 && activeProduct">
            <easy-charts
              :type="type"
              :module="type === 'module' ? activeProduct : null"
              :driver="type === 'driver' ? activeProduct : null"
              :region="region"
            />
          </template>
          <span class="header-name">{{ header.name }}</span>
        </div>

        <div
          class="compare-product"
          v-for="(item, index) in items"
          :key="index"
          @click="onProductSelect(item)"
        >
          <div
            v-for="(header, index) in headers"
            class="compare-col item-col"
            :class="{
              'product-top': index === 0,
              'product-bottom': index === headers.length - 1,
              active:
                selected &&
                item.uid === selected.uid &&
                calculateParametersCheck(selected, item),
            }"
            :style="{ order: index + 1 }"
            :key="item.uid + header.key"
          >
            <span
              v-if="header.valueFormatter"
              v-html="valueFormatter(header, item)"
            ></span>
            <span v-else>{{ get(item, header.key) || "-" }}</span>
          </div>
        </div>

        <!-- Dummy columns -->
        <!-- eslint-disable -->
        <template v-for="_ in dummyColumns">
          <div
            v-for="(_, i) in headers"
            class="dummy-col"
            :style="{ order: i + 1 }"
          />
        </template>
        <!-- eslint-enable -->
      </div>

      <div class="component-button">
        <input
          type="submit"
          class="btn btn-secondary btn-block"
          value="Add to current solution"
          @click="onAdd"
          :disabled="!selected"
        />
        <input
          type="button"
          class="btn btn-outline-secondary btn-block"
          value="Cancel"
          @click="onCancel"
        />
        <div class="spacer" />
        <input
          type="button"
          class="btn btn-secondary btn-block"
          value="Download"
          @click="onDownload"
        />
      </div>
    </div>
  </Modal>
</template>

<script>
import { get } from "lodash";
import { mapState } from "vuex";
import { MAX_COMPARE_PRODUCTS } from "@/service/Constants";
import { formatNumber } from "@/utils/FormatUtils";
import { http } from "@/services/http";
import saveAs from "file-saver";

export default {
  name: "CompareItemsModal",
  props: {
    type: {
      type: String,
      required: true,
    },
    items: {
      type: Array,
      required: false,
      default: () => [],
    },
    headers: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  computed: {
    ...mapState("brain", ["region"]),
    dummyColumns() {
      return MAX_COMPARE_PRODUCTS - this.items.length;
    },
  },
  data() {
    return {
      show: true,
      selected: null,
      activeProduct: null,
    };
  },
  methods: {
    get(obj, path) {
      return get(obj, path);
    },
    valueFormatter(header, item) {
      let value = this.get(item, header.key);
      if (header.key === "driverCalculationResult.outputPower") {
        const current = this.get(item, "driverCalculationResult.outputCurrent");
        const voltage = this.get(item, "driverCalculationResult.outputVoltage");
        if (current && voltage) {
          value = formatNumber(current * voltage, "1");
        }
      }
      return value !== null && value !== undefined && value !== ""
        ? header.valueFormatter(value)
        : "-";
    },
    onProductSelect(product) {
      if (
        this.selected &&
        this.selected.uid === product.uid &&
        this.calculateParametersCheck(this.selected, product)
      ) {
        this.selected = null;
        this.activeProduct = null;
      } else {
        this.selected = product;
        this.activeProduct = product;
      }
    },
    onCancel() {
      this.show = false;
    },
    onAdd() {
      this.$emit("addToSolution", this.selected);
      this.show = false;
    },
    onDownload() {
      this.$store.commit("brain/global/setBusy", true, { root: true });
      http
        .post(`/api/v2/${this.type}s/comparison/export`, this.items, {
          responseType: "arraybuffer",
        })
        .then((response) => {
          this.$store.commit("brain/global/setBusy", false, { root: true });
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;",
          });
          const date = new Date();
          const filename = `${this.type}-comparison-export-${date.getTime()}`;
          saveAs(blob, filename + ".xlsx");
        });
    },
    calculateParametersCheck(m, module) {
      if (!m.calculationResult && !module.calculationResult) return false;
      return (
        m.calculationResult.luminousFluxPerModule ===
          module.calculationResult.luminousFluxPerModule &&
        m.calculationResult.operatingCurrent ===
          module.calculationResult.operatingCurrent &&
        m.calculationResult.temperature === module.calculationResult.temperature
      );
    },
  },
};
</script>

<style lang="scss" scoped>
$border-size: 1px;
$border-style: solid;
$active-color: $primary;
$inactive-color: $mid-gray-400;
$active-border: $border-size $border-style $active-color;
$inactive-border: $border-size $border-style $inactive-color;

$header-width: 19%;
$item-width: calc(27% - 20px);

.component {
  display: flex;
  flex-flow: column wrap;
  justify-content: flex-start;

  .component-title {
    color: $blue;
    font-family: $font-family-sans-serif;
    font-weight: $btn-font-weight;
  }

  .compare-table {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-around;
    align-content: stretch;
    align-items: stretch;

    .compare-product {
      display: contents;
      cursor: pointer;
    }

    .compare-col {
      flex: 0 1 auto;
      align-self: auto;
      padding: 12px;

      &.header-col {
        width: $header-width;

        .header-name {
          font-weight: bold;
        }
      }

      &.item-col {
        text-align: center;
        border-left: $inactive-border;
        border-right: $inactive-border;
        margin-left: 20px;
        width: $item-width;

        &.active {
          border-left: $active-border;
          border-right: $active-border;
        }

        &.product-top {
          border-top: $inactive-border;

          &.active {
            border-top: $active-border;
          }
        }

        &.product-bottom {
          padding-bottom: 25px;
          border-bottom: $inactive-border;

          &.active {
            border-bottom: $active-border;
          }
        }
      }
    }

    .dummy-col {
      flex: 0 1 auto;
      align-self: auto;
      padding: 10px;
      margin-left: 20px;
      width: $item-width;
      visibility: hidden;
    }
  }

  .component-paragraph {
    font-size: 16px;
  }

  .component-button {
    display: flex;
    flex-direction: row-reverse;
    flex-wrap: wrap;
    width: 100%;
    padding-top: 20px;

    input {
      width: $item-width;
      margin-left: 20px;
    }

    input:last-child {
      width: $header-width;
      margin-left: 0;
    }

    .spacer {
      flex: 0 1 auto;
      margin-left: 20px;
      width: $item-width;
      visibility: hidden;
    }

    .btn-block + .btn-block {
      margin-top: 0;
    }
  }
}
</style>

<style lang="scss">
.compare-modal .modal-wrapper .modal-container {
  max-width: 70% !important;
}
</style>
